import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitCompare, Sparkles, TrendingUp, Coins } from "lucide-react";
import { api } from "@/lib/api";
import { EDI } from "@/constants/testIds";

const COMMODITIES = ["Gold","Copper","PGM","Coal","Iron Ore","Lithium","Nickel","Cobalt","Green Hydrogen","Water Treatment","Fertiliser"];

export default function ReuseIntelligence() {
    const [cfg, setCfg] = useState({ commodity: "Gold", process: "CIP", client: "Sibanye-Stillwater", capacity: "4 Mtpa" });
    const [pkg, setPkg] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => { compute(cfg); /* eslint-disable-next-line */ }, []);

    const compute = async (nextCfg) => {
        setBusy(true);
        try {
            const { data } = await api.post("/edi/reuse-intelligence", nextCfg || cfg);
            setPkg(data);
        } catch { toast.error("Reuse Intelligence failed"); }
        finally { setBusy(false); }
    };

    const set = (k, v) => {
        const next = { ...cfg, [k]: v };
        setCfg(next); compute(next);
    };

    return (
        <div className="space-y-8" data-testid={EDI.reuseIntelligenceRoot}>
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Enterprise Delivery Intelligence™</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Engineering Reuse Intelligence™
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Twelve dimensions of similarity across historical projects. Every new
                        opportunity is compared to the best-in-class historical reference and
                        scored by commodity, scope, equipment, plant, engineering, drawings,
                        data sheets, specifications, lessons and estimated savings.
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[var(--sgsb-gold)] text-xs font-body">
                    <GitCompare className="h-4 w-4" strokeWidth={1.5} />
                    <span className="kicker text-[10px]">12-dimension similarity</span>
                </div>
            </div>

            <div className="glass rounded-xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    {[
                        ["Commodity", "commodity", COMMODITIES, EDI.reuseIntelCommodity],
                        ["Process",   "process",   ["CIP","CIL","SX-EW","Smelter","Beneficiation","HPAL","Hydroxide refinery","Desalination (RO)","Electrolysis + Storage","Ammonia"], EDI.reuseIntelProcess],
                        ["Client",    "client",    ["Sibanye-Stillwater","Anglo American","Kumba Iron Ore","Harmony Gold","First Quantum","Khoemacau Copper","OCP Africa","Hive Hydrogen"], EDI.reuseIntelClient],
                        ["Capacity",  "capacity",  ["1 Mtpa","2 Mtpa","4 Mtpa","6 Mtpa","8 Mtpa","12 Mtpa"], EDI.reuseIntelCapacity],
                    ].map(([label, key, options, tid]) => (
                        <div key={key}>
                            <label className="kicker text-slate-500 text-[10px] mb-1 block">{label}</label>
                            <select data-testid={tid} value={cfg[key]} onChange={(e) => set(key, e.target.value)}
                                className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-3 py-2 text-sm font-body text-slate-100 outline-none focus:border-[color:var(--sgsb-gold)]"
                            >
                                {options.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        </div>
                    ))}
                </div>
            </div>

            {busy && !pkg && <div className="kicker text-slate-500">Computing 12-dimension similarity…</div>}

            {pkg && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { l: "Reference basis",  v: `${pkg.overall_similarity_pct}%`, s: pkg.reference, Icon: GitCompare },
                            { l: "Hours saved",      v: pkg.hours_saved.toLocaleString(), s: "vs greenfield", Icon: TrendingUp },
                            { l: "Cost saved",       v: pkg.cost_saved,                    s: "blended $150/hr", Icon: Coins },
                            { l: "Confidence",       v: `${pkg.confidence}%`,              s: "board-ready", Icon: Sparkles },
                        ].map(({ l, v, s, Icon }) => (
                            <div key={l} className="glass hover-lift rounded-xl p-5 relative overflow-hidden">
                                <div className="absolute top-0 left-0 h-[2px] w-10 bg-[var(--sgsb-gold)]" />
                                <Icon className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                                <div className="mt-2 kicker text-slate-400 text-[10px]">{l}</div>
                                <div className="mt-2 font-display font-black text-white text-2xl tracking-tighter">{v}</div>
                                <div className="mt-1 text-[11px] text-slate-500 font-body">{s}</div>
                            </div>
                        ))}
                    </div>

                    <div className="glass rounded-xl overflow-hidden">
                        <div className="p-4 border-b border-slate-800/60 flex items-center gap-2">
                            <GitCompare className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                            <h3 className="font-display font-black text-white tracking-tight">Similarity by dimension</h3>
                            <div className="flex-1" />
                            <span className="kicker text-[10px] text-slate-500">12 dimensions</span>
                        </div>
                        <table className="w-full text-sm font-body">
                            <thead>
                                <tr className="text-slate-500 kicker text-[10px]">
                                    <th className="text-left px-4 py-3">Dimension</th>
                                    <th className="text-right px-4 py-3">Weight</th>
                                    <th className="text-left px-4 py-3">Similarity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {pkg.dimensions.map((d) => (
                                    <tr key={d.dimension} className="border-t border-slate-800/60">
                                        <td className="px-4 py-2 text-slate-100">{d.dimension}</td>
                                        <td className="px-4 py-2 text-right text-slate-400">{(d.weight * 100).toFixed(0)}%</td>
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
                                                    <div className="h-full bg-[var(--sgsb-gold)]" style={{ width: `${d.similarity}%` }} />
                                                </div>
                                                <span className="text-[var(--sgsb-gold)] font-display font-bold text-sm w-10 text-right">{d.similarity}%</span>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="glass rounded-xl p-6 relative overflow-hidden">
                        <div className="absolute inset-0 opacity-40 pointer-events-none"
                             style={{ background: "radial-gradient(ellipse at 90% 10%, rgba(16,185,129,0.20), transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(212,175,55,0.16), transparent 55%)" }} />
                        <div className="relative">
                            <div className="kicker text-[var(--sgsb-gold)]">Executive recommendation</div>
                            <p className="mt-2 text-slate-200 font-body text-base leading-relaxed">{pkg.recommendation}</p>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
